import React from "react";
import { Controller, useWatch } from "react-hook-form";

import {
  FormInput,
  FormMoneyInput,
  InputDate,
} from "../../../../components/Input";
import { formatCurrency } from "../../../../utils";
import { ResponsiveFormSelect } from "../ResponsiveFormSelect";
import { FROM_ACCOUNT_SHEET_TITLE } from "./constants";
import { PaymentFormFieldsProps } from "./types";
import {
  Field,
  FieldLabel,
  FormCard,
  LabelRow,
} from "../../../../components/FormPanel";
import { ResponsiveRow } from "../styles";
import {
  AppliedCard,
  AppliedHeader,
  AppliedLabel,
  BalanceLabel,
  BalanceRow,
  BalanceValue,
  Divider,
  LegendHead,
  LegendItem,
  LegendSwatch,
  LegendValue,
  ManualCheckbox,
  ManualToggle,
  SplitBar,
  SplitLegend,
  SplitPrincipal,
} from "./styles";

const EMPTY = "—";

export const PaymentFormFields: React.FC<PaymentFormFieldsProps> = ({
  control,
  isSubmitting,
  isCompact,
  accountOptions,
  currentAmount,
  validationRules,
}) => {
  const amountRaw = useWatch({ control, name: "amount" });
  const principalRaw = useWatch({ control, name: "principalAmount" });
  const setManually = useWatch({ control, name: "setPrincipalManually" });

  const amount = Number.parseFloat(String(amountRaw ?? ""));
  const principal = Number.parseFloat(String(principalRaw ?? ""));
  const hasAmount = Number.isFinite(amount) && amount > 0;
  const safePrincipal = Number.isFinite(principal) ? principal : 0;
  const interest = amount - safePrincipal;
  const principalPercent = hasAmount
    ? Math.min(100, Math.max(0, (safePrincipal / amount) * 100))
    : 0;

  const hasBalance = currentAmount !== null && currentAmount !== undefined;
  const balanceAfter = hasBalance ? currentAmount - safePrincipal : null;

  const principalDisplay = hasAmount ? formatCurrency(safePrincipal) : EMPTY;
  const interestDisplay = hasAmount ? formatCurrency(interest) : EMPTY;
  const balanceDisplay =
    hasAmount && balanceAfter !== null ? formatCurrency(balanceAfter) : EMPTY;

  return (
    <>
      <FormCard>
        <ResponsiveRow>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="payment-date">Payment date</FieldLabel>
            </LabelRow>
            <InputDate
              id="payment-date"
              rules={validationRules.paymentDate}
              name="paymentDate"
              control={control}
              placeholder="YYYY/MM/DD"
              maxDate={new Date()}
              disabled={isSubmitting}
            />
          </Field>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="payment-amount">
                Total amount (€) *
              </FieldLabel>
            </LabelRow>
            <FormMoneyInput
              id="payment-amount"
              required
              autoFocus={true}
              rules={validationRules.amount}
              name="amount"
              placeholder="€ 0.00"
              control={control}
              disabled={isSubmitting}
            />
          </Field>
        </ResponsiveRow>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="from-account-select">
              From account *
            </FieldLabel>
          </LabelRow>
          <ResponsiveFormSelect
            isCompact={isCompact}
            testId="from-account-select"
            name="fromAccount"
            control={control}
            rules={validationRules.fromAccount}
            options={accountOptions}
            sheetTitle={FROM_ACCOUNT_SHEET_TITLE}
            placeholder="Select account"
            disabled={isSubmitting}
          />
        </Field>
      </FormCard>

      <AppliedCard>
        <AppliedHeader>
          <AppliedLabel>Applied as</AppliedLabel>
          <ManualToggle>
            <Controller
              name="setPrincipalManually"
              control={control}
              render={({ field: { value, onChange } }) => (
                <ManualCheckbox
                  size="small"
                  checked={Boolean(value)}
                  onChange={(event) => onChange(event.target.checked)}
                  disabled={isSubmitting}
                />
              )}
            />
            Set principal manually
          </ManualToggle>
        </AppliedHeader>

        <SplitBar
          role="img"
          aria-label={`Principal ${principalDisplay}, interest ${interestDisplay}`}
        >
          <SplitPrincipal $pct={principalPercent} />
        </SplitBar>

        <SplitLegend>
          <LegendItem $align="start">
            <LegendHead>
              <LegendSwatch $kind="principal" />
              Principal
            </LegendHead>
            <LegendValue $kind="principal">{principalDisplay}</LegendValue>
          </LegendItem>
          <LegendItem $align="end">
            <LegendHead>
              Interest
              <LegendSwatch $kind="interest" />
            </LegendHead>
            <LegendValue $kind="interest">{interestDisplay}</LegendValue>
          </LegendItem>
        </SplitLegend>

        {setManually ? (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="payment-principal">
                Principal amount (€) *
              </FieldLabel>
            </LabelRow>
            <FormMoneyInput
              id="payment-principal"
              required
              rules={validationRules.principalAmount}
              name="principalAmount"
              control={control}
              placeholder="€ 0.00"
              disabled={isSubmitting}
            />
          </Field>
        ) : null}

        <Divider />

        <BalanceRow>
          <BalanceLabel>Balance after this payment</BalanceLabel>
          <BalanceValue>{balanceDisplay}</BalanceValue>
        </BalanceRow>
      </AppliedCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="payment-note">Note</FieldLabel>
          </LabelRow>
          <FormInput
            id="payment-note"
            rules={validationRules.note}
            name="note"
            control={control}
            handleClear={false}
            placeholder="e.g. year-end overpayment"
            disabled={isSubmitting}
          />
        </Field>
      </FormCard>
    </>
  );
};
